import { forwardRef } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { buttonTap } from "@styles/motion";

/**
 * Button — the single button primitive for the whole app.
 *
 * @param {"primary"|"secondary"|"premium"|"outline"|"ghost"|"danger"} variant
 * @param {"sm"|"md"|"lg"} size
 * @param {boolean} fullWidth
 * @param {boolean} isLoading
 * @param {boolean} disabled
 * @param {React.ReactNode} leftIcon
 * @param {React.ReactNode} rightIcon
 *
 * Usage:
 *   <Button variant="primary" onClick={...}>Bog'lanish</Button>
 *   <Button variant="premium" leftIcon={<Sparkles size={16} />}>Premium qilish</Button>
 */
const VARIANT_CLASSES = {
  primary:
    "bg-brand-primary text-white hover:bg-brand-primary-dark active:bg-brand-primary-dark disabled:bg-brand-line disabled:text-brand-muted",
  secondary:
    "bg-white text-brand-ink border border-brand-line hover:border-brand-primary disabled:text-brand-muted disabled:border-brand-line",
  premium:
    "bg-gold-gradient text-white shadow-gold-glow hover:brightness-105 disabled:bg-brand-line disabled:text-brand-muted disabled:shadow-none",
  outline:
    "bg-transparent text-brand-primary border-[1.5px] border-brand-primary hover:bg-brand-primary/5 disabled:text-brand-muted disabled:border-brand-line",
  ghost:
    "bg-transparent text-brand-ink hover:bg-brand-line/40 disabled:text-brand-muted",
  danger:
    "bg-brand-danger text-white hover:brightness-95 disabled:bg-brand-line disabled:text-brand-muted",
};

const SIZE_CLASSES = {
  sm: "h-9 px-3.5 text-body-sm gap-1.5 rounded-md",
  md: "h-12 px-5 text-body-md gap-2 rounded-lg",
  lg: "h-14 px-6 text-body-lg gap-2 rounded-xl",
};

const Button = forwardRef(function Button(
  {
    children,
    variant = "primary",
    size = "md",
    fullWidth = false,
    isLoading = false,
    disabled = false,
    leftIcon = null,
    rightIcon = null,
    type = "button",
    className = "",
    ...rest
  },
  ref
) {
  const isDisabled = disabled || isLoading;

  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={isDisabled}
      whileTap={isDisabled ? undefined : buttonTap.whileTap}
      className={[
        "inline-flex items-center justify-center font-body font-semibold",
        "transition-colors duration-150 ease-royal-ease select-none",
        "disabled:cursor-not-allowed",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth ? "w-full" : "",
        className,
      ].join(" ")}
      {...rest}
    >
      {isLoading ? (
        <Loader2 size={size === "sm" ? 14 : 18} className="animate-spin" />
      ) : (
        leftIcon
      )}
      {children && <span>{children}</span>}
      {!isLoading && rightIcon}
    </motion.button>
  );
});

export default Button;
